import { useEffect, useState } from 'react';
import { Form, Button, Col } from 'react-bootstrap';
import { useWatch, Controller } from 'react-hook-form';
import { recursiveMap } from '../../../utilities/treeHelpers';
import TreeViewModal from '../../../components/TreeViewModal';
import NodeIcon from '../../../components/NodeIcon';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getSharedUnitFamiliesBetweenProfilesApi } from '../emissionProfileApi';
import { parseUserInfo, transErrorMessage } from '../../../utilities/common';

const EmissionSourceLink = ({ control, setValue, tree, optEnabledCustomFactor, customEmissionFactors, emissionProfile }) => {
  const { t } = useTranslation();

  const [showTreeSelector, setShowTreeSelector] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isLoadingId, setIsLoadingId] = useState(null);
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));

  const openModal = () => setShowTreeSelector(true);
  const closeModal = () => setShowTreeSelector(false);

  const eprflsrcLinkedId = useWatch({
    control,
    name: 'eprflsrcLinkedId'
  });

  const linked = useWatch({
    control,
    name: 'linked'
  });

  const handleChangeLinkedEps = async (n) => {
    try {
      if (isLoadingId) {
        return;
      };
      setIsLoadingId(n.eprflsrcId);
      const data = {
        ...emissionProfile,
        targetEpsId: n?.eprflsrcId
      };
      const shareUnits = await getSharedUnitFamiliesBetweenProfilesApi(orgId, data);
      if (shareUnits.length) {
        setValue('eprflsrcLinkedId', n.eprflsrcId, { shouldDirty: true });
        setSelectedNode(n);
        closeModal();
      } else {
        toast.error(t('Cannot link to the selected emission profile source: no matching unit families found.'));
      }
    } catch (e) {
      toast.error(transErrorMessage(t, e));
    } finally {
      setIsLoadingId(null);
    }
  };
  const fullFuelCycleTree = {
    ...tree,
    children: tree?.children.filter(category => category.eprflcatId === tree.fullFuelCycleScopeId),
  };
  const recursiveTree = recursiveMap(fullFuelCycleTree, (n) => {
    if (n.eprflId) {
      return {
        weight: n.weight,
        value: n.eprflId,
        labelRaw: n.eprflName,
        label: <span>{n.eprflName}</span>,
        isActive: false
      };
    }
    if (n.eprflcatId) {
      return {
        weight: n.weight,
        value: n.eprflcatId,
        labelRaw: n.eprflcatName,
        label: (
          <span>
            <NodeIcon node={n} />
            {n.eprflcatName}
          </span>
        ),
        isActive: false
      };
    }
    if (n.eprflsrcId) {
      return {
        weight: n.weight,
        value: n.eprflsrcId,
        labelRaw: n.eprflsrcName,
        label: (
          <span style={{ cursor: 'pointer' }} onClick={() => handleChangeLinkedEps(n)}>
            <NodeIcon node={{...n, isLoading: isLoadingId === n.eprflsrcId}} />
            {n.eprflsrcName}
          </span>
        ),
        isActive:
          (typeof eprflsrcLinkedId === 'object' && eprflsrcLinkedId !== null
            ? eprflsrcLinkedId.eprflsrcId
            : eprflsrcLinkedId) === n.eprflsrcId
      };
    }
  });
  recursiveTree.isRoot = true;

  useEffect(() => {
    if (typeof eprflsrcLinkedId === 'object' && eprflsrcLinkedId !== null) {
      setSelectedNode(eprflsrcLinkedId);
      setValue('eprflsrcLinkedId', eprflsrcLinkedId.eprflsrcId);
    }
  }, [eprflsrcLinkedId, setValue]);

  const isDisabled = !emissionProfile.eprflsrcEsrcId && ((optEnabledCustomFactor && !customEmissionFactors?.length)
    || (!optEnabledCustomFactor && !emissionProfile.eprflsrcConsumptionUnit));
  return (
    <>
      <Col>
        <Form.Group className="custom-form-control">
          <Controller
            name="linked"
            control={control}
            render={({ field }) => {
              const parsed = field.value === 'Y' ? true : false;
              const format = () => {
                field.onChange(field.value === 'Y' ? 'N' : 'Y');
                setValue('eprflsrcLinkedId', null);
                setSelectedNode(null);
              };
              return (
                <Form.Check
                  custom
                  id="linked"
                  onChange={format}
                  checked={parsed}
                  disabled={isDisabled}
                  label={t('Full Fuel Cycle')}
                />
              );
            }}
          />
        </Form.Group>
      </Col>
      {linked === 'Y' && (
        <Col>
          <Form.Group className="custom-form-control">
            <Form.Label>{t('Linked EPS Full Fuel Cycle')}</Form.Label>
            <div>
              <Button disabled={isDisabled} onClick={openModal} variant="outline-secondary">
                {selectedNode ? t('Select') : t('Select')}
              </Button>
              {selectedNode && <span className="ml-3">{selectedNode.eprflsrcName}</span>}
            </div>
          </Form.Group>
          <TreeViewModal
            tree={recursiveTree}
            closeModal={closeModal}
            show={showTreeSelector}
            title={t('Select Emission Profile')}
           />
        </Col>
      )}
    </>
  );
};

export default EmissionSourceLink;
